import React, { useState, useEffect, useCallback } from 'react';
import { documentsApi, clientsApi, contractsApi, vehiclesApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ExpedienteTab from './documents/ExpedienteTab';
import {
  FileText,
  Upload,
  Trash,
  DownloadSimple,
  MagnifyingGlass,
  FolderOpen,
  File,
  FilePdf,
  FileImage,
  User,
  X,
  Warning,
  Plus,
  FunnelSimple
} from '@phosphor-icons/react';

const DOC_TYPES = {
  general: 'General',
  ine: 'INE / Identificación',
  comprobante: 'Comprobante de Domicilio',
  licencia: 'Licencia de Conducir',
  rfc: 'Constancia RFC',
  contrato_firmado: 'Contrato Firmado',
  pagare: 'Pagaré',
  otro: 'Otro'
};

const getFileIcon = (contentType) => {
  if (!contentType) return File;
  if (contentType.includes('pdf')) return FilePdf;
  if (contentType.includes('image')) return FileImage;
  return File;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Documents = () => {
  const { canManage, canEdit } = useAuth();
  const [activeTab, setActiveTab] = useState('contracts');
  const [contractDocs, setContractDocs] = useState([]);
  const [clientDocs, setClientDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadClientId, setUploadClientId] = useState('');
  const [uploadContractId, setUploadContractId] = useState('');
  const [uploadDocType, setUploadDocType] = useState('general');
  const [uploadDescription, setUploadDescription] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [contractDocsRes, clientDocsRes, clientsRes, contractsRes, vehiclesRes] = await Promise.all([
        documentsApi.listContractDocs(),
        documentsApi.listClientDocs(),
        clientsApi.getAll(),
        contractsApi.getAll(),
        vehiclesApi.getAll()
      ]);
      setContractDocs(contractDocsRes.data);
      setClientDocs(clientDocsRes.data);
      setClients(clientsRes.data);
      setContracts(contractsRes.data);
      setVehicles(vehiclesRes.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const getClientName = useCallback((id) => {
    return clients.find(c => c.id === id)?.name || 'Sin asignar';
  }, [clients]);

  const getContractLabel = useCallback((id) => {
    const c = contracts.find(ct => ct.id === id);
    if (!c) return 'Sin contrato';
    const v = vehicles.find(v => v.id === c.vehicle_id);
    const clientName = clients.find(cl => cl.id === c.client_id)?.name || '';
    return `${clientName} - ${v ? v.brand + ' ' + v.sub_brand + ' (' + v.plates + ')' : ''}`;
  }, [contracts, vehicles, clients]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;

    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('description', uploadDescription);

    try {
      if (activeTab === 'contracts') {
        if (uploadContractId) formData.append('contract_id', uploadContractId);
        if (uploadClientId) formData.append('client_id', uploadClientId);
        await documentsApi.uploadContractDoc(formData);
      } else {
        if (uploadClientId) formData.append('client_id', uploadClientId);
        formData.append('doc_type', uploadDocType);
        await documentsApi.uploadClientDoc(formData);
      }
      setShowUploadModal(false);
      resetUploadForm();
      loadData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId, type) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      if (type === 'contract') {
        await documentsApi.deleteContractDoc(fileId);
      } else {
        await documentsApi.deleteClientDoc(fileId);
      }
      loadData();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadClientId('');
    setUploadContractId('');
    setUploadDocType('general');
    setUploadDescription('');
  };

  const filteredContractDocs = contractDocs.filter(d => {
    const matchSearch = !searchTerm || d.original_name?.toLowerCase().includes(searchTerm.toLowerCase()) || d.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchClient = !filterClient || d.client_id === filterClient;
    return matchSearch && matchClient;
  });

  const filteredClientDocs = clientDocs.filter(d => {
    const matchSearch = !searchTerm || d.original_name?.toLowerCase().includes(searchTerm.toLowerCase()) || d.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchClient = !filterClient || d.client_id === filterClient;
    return matchSearch && matchClient;
  });

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Documentos</h1>
          <p className="text-[#64748B]">Repositorio de contratos escaneados y documentos de clientes</p>
        </div>
        {canManage && (
          <button
            onClick={() => { resetUploadForm(); setShowUploadModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="upload-doc-btn"
          >
            <Upload size={18} /> Subir Documento
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger flex items-center gap-2"><Warning size={20} />{error}<button onClick={() => setError('')} className="ml-auto"><X size={16} /></button></div>}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F1F5F9] p-1 rounded-lg w-fit" data-testid="documents-tabs">
        <button
          onClick={() => { setActiveTab('contracts'); setSearchTerm(''); setFilterClient(''); }}
          className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'contracts' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`}
          data-testid="tab-contracts"
        >
          <div className="flex items-center gap-2">
            <FileText size={18} />
            Contratos Escaneados
            <span className="text-xs bg-[#EFF6FF] text-[#0055FF] px-2 py-0.5 rounded-full">{contractDocs.length}</span>
          </div>
        </button>
        <button
          onClick={() => { setActiveTab('clients'); setSearchTerm(''); setFilterClient(''); }}
          className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'clients' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`}
          data-testid="tab-clients"
        >
          <div className="flex items-center gap-2">
            <User size={18} />
            Documentos de Clientes
            <span className="text-xs bg-[#EFF6FF] text-[#0055FF] px-2 py-0.5 rounded-full">{clientDocs.length}</span>
          </div>
        </button>
        <button
          onClick={() => { setActiveTab('expediente'); setSearchTerm(''); setFilterClient(''); }}
          className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'expediente' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`}
          data-testid="tab-expediente"
        >
          <div className="flex items-center gap-2">
            <FolderOpen size={18} />
            Expediente (Matriz)
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input
            type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre de archivo..."
            className="input pl-10" data-testid="search-docs-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <FunnelSimple size={18} className="text-[#64748B]" />
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)} className="input w-56" data-testid="filter-client-select">
            <option value="">Todos los clientes</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Documents Grid */}
      {activeTab === 'contracts' && (
        <div data-testid="contract-docs-section">
          {filteredContractDocs.length === 0 ? (
            <div className="card p-12 text-center">
              <FolderOpen size={48} className="mx-auto text-[#CBD5E1] mb-4" />
              <p className="text-[#64748B] text-lg">No hay contratos escaneados</p>
              <p className="text-sm text-[#94A3B8] mt-1">Sube archivos PDF o imágenes de contratos firmados</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredContractDocs.map(doc => {
                const Icon = getFileIcon(doc.content_type);
                return (
                  <div key={doc.file_id} className="card p-4 hover:shadow-md transition-shadow" data-testid={`contract-doc-${doc.file_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
                        <Icon size={22} className="text-[#0055FF]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0F172A] truncate" title={doc.original_name}>{doc.original_name}</p>
                        <p className="text-xs text-[#64748B] mt-0.5">{formatFileSize(doc.size)}</p>
                        {doc.client_id && <p className="text-xs text-[#0055FF] mt-1">{getClientName(doc.client_id)}</p>}
                        {doc.contract_id && <p className="text-xs text-[#64748B] mt-0.5">{getContractLabel(doc.contract_id)}</p>}
                        {doc.description && <p className="text-xs text-[#94A3B8] mt-1 italic">{doc.description}</p>}
                        <p className="text-[10px] text-[#94A3B8] mt-2">{new Date(doc.created_at).toLocaleDateString('es-MX')} por {doc.uploaded_by_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#F1F5F9]">
                      <a
                        href={documentsApi.downloadContractDoc(doc.file_id)}
                        target="_blank" rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0055FF] bg-[#EFF6FF] rounded-lg hover:bg-[#DBEAFE] transition-colors"
                        data-testid={`download-contract-doc-${doc.file_id}`}
                      >
                        <DownloadSimple size={14} /> Descargar
                      </a>
                      {canManage && (
                        <button
                          onClick={() => handleDelete(doc.file_id, 'contract')}
                          className="px-3 py-1.5 text-xs text-[#E11D48] bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          data-testid={`delete-contract-doc-${doc.file_id}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'expediente' && (
        <ExpedienteTab canValidate={canEdit} canDelete={canManage} />
      )}

      {activeTab === 'clients' && (
        <div data-testid="client-docs-section">
          {filteredClientDocs.length === 0 ? (
            <div className="card p-12 text-center">
              <FolderOpen size={48} className="mx-auto text-[#CBD5E1] mb-4" />
              <p className="text-[#64748B] text-lg">No hay documentos de clientes</p>
              <p className="text-sm text-[#94A3B8] mt-1">Sube INE, comprobantes de domicilio, licencias, etc.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClientDocs.map(doc => {
                const Icon = getFileIcon(doc.content_type);
                return (
                  <div key={doc.file_id} className="card p-4 hover:shadow-md transition-shadow" data-testid={`client-doc-${doc.file_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                        <Icon size={22} className="text-[#10B981]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0F172A] truncate" title={doc.original_name}>{doc.original_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-[#64748B]">{formatFileSize(doc.size)}</span>
                          {doc.doc_type && doc.doc_type !== 'general' && (
                            <span className="text-[10px] bg-[#F1F5F9] text-[#475569] px-1.5 py-0.5 rounded">{DOC_TYPES[doc.doc_type] || doc.doc_type}</span>
                          )}
                        </div>
                        {doc.client_id && <p className="text-xs text-[#10B981] mt-1 font-medium">{getClientName(doc.client_id)}</p>}
                        {doc.description && <p className="text-xs text-[#94A3B8] mt-1 italic">{doc.description}</p>}
                        <p className="text-[10px] text-[#94A3B8] mt-2">{new Date(doc.created_at).toLocaleDateString('es-MX')} por {doc.uploaded_by_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#F1F5F9]">
                      <a
                        href={documentsApi.downloadClientDoc(doc.file_id)}
                        target="_blank" rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#10B981] bg-[#F0FDF4] rounded-lg hover:bg-[#DCFCE7] transition-colors"
                        data-testid={`download-client-doc-${doc.file_id}`}
                      >
                        <DownloadSimple size={14} /> Descargar
                      </a>
                      {canManage && (
                        <button
                          onClick={() => handleDelete(doc.file_id, 'client')}
                          className="px-3 py-1.5 text-xs text-[#E11D48] bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          data-testid={`delete-client-doc-${doc.file_id}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" data-testid="upload-modal">
          <div className="modal-content animate-fade-in max-w-lg">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">
                  {activeTab === 'contracts' ? 'Subir Contrato Escaneado' : 'Subir Documento de Cliente'}
                </h2>
                <button onClick={() => setShowUploadModal(false)} className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"><X size={20} /></button>
              </div>
            </div>
            <form onSubmit={handleUpload} className="p-6 space-y-4">
              {/* File input */}
              <div>
                <label className="label">Archivo *</label>
                <div className="border-2 border-dashed border-[#CBD5E1] rounded-xl p-6 text-center hover:border-[#0055FF] transition-colors cursor-pointer"
                  onClick={() => document.getElementById('file-input').click()}
                >
                  {uploadFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <File size={24} className="text-[#0055FF]" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-[#0F172A]">{uploadFile.name}</p>
                        <p className="text-xs text-[#64748B]">{formatFileSize(uploadFile.size)}</p>
                      </div>
                      <button type="button" onClick={e => { e.stopPropagation(); setUploadFile(null); }} className="text-[#E11D48]"><X size={18} /></button>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} className="mx-auto text-[#CBD5E1] mb-2" />
                      <p className="text-sm text-[#64748B]">Haz clic para seleccionar archivo</p>
                      <p className="text-xs text-[#94A3B8] mt-1">PDF, JPG, PNG hasta 50 MB</p>
                    </>
                  )}
                </div>
                <input
                  id="file-input" type="file" className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.tiff,.bmp,.doc,.docx"
                  onChange={e => setUploadFile(e.target.files[0] || null)}
                  data-testid="file-input"
                />
              </div>

              {/* Client */}
              <div>
                <label className="label">Cliente</label>
                <select value={uploadClientId} onChange={e => setUploadClientId(e.target.value)} className="input" data-testid="upload-client-select">
                  <option value="">Seleccionar cliente...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* Contract (only for contracts tab) */}
              {activeTab === 'contracts' && (
                <div>
                  <label className="label">Contrato asociado</label>
                  <select value={uploadContractId} onChange={e => setUploadContractId(e.target.value)} className="input" data-testid="upload-contract-select">
                    <option value="">Seleccionar contrato...</option>
                    {contracts.map(c => {
                      const clientName = clients.find(cl => cl.id === c.client_id)?.name || '';
                      const v = vehicles.find(v => v.id === c.vehicle_id);
                      return <option key={c.id} value={c.id}>{clientName} - {v ? v.plates : ''} ({c.start_date})</option>;
                    })}
                  </select>
                </div>
              )}

              {/* Doc Type (only for clients tab) */}
              {activeTab === 'clients' && (
                <div>
                  <label className="label">Tipo de Documento</label>
                  <select value={uploadDocType} onChange={e => setUploadDocType(e.target.value)} className="input" data-testid="upload-doctype-select">
                    {Object.entries(DOC_TYPES).map(([val, label]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="label">Descripción (opcional)</label>
                <input
                  type="text" value={uploadDescription} onChange={e => setUploadDescription(e.target.value)}
                  className="input" placeholder="Ej: Contrato firmado original, INE frente..."
                  data-testid="upload-description-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowUploadModal(false)} className="btn-secondary">Cancelar</button>
                <button type="submit" disabled={!uploadFile || uploading} className="btn-primary flex items-center gap-2" data-testid="submit-upload-btn">
                  {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={18} />}
                  Subir
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
